import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Input,
  LoadingIcon,
  Modal,
  Selector,
  Toggle,
  Tooltip,
} from 'vet-bones/design-system/components';
import { SelectItem } from 'vet-bones/design-system/utils/select';
import { useDidValueChange } from 'vet-bones/utils/hooks';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  EDIT_ENDPOINT_DIALOG_CANCEL,
  EDIT_ENDPOINT_DIALOG_ENDPOINT,
  EDIT_ENDPOINT_DIALOG_INTERNAL_ENDPOINT,
  EDIT_ENDPOINT_DIALOG_IS_DEFAULT,
  EDIT_ENDPOINT_DIALOG_NAME,
  EDIT_ENDPOINT_DIALOG_PASSWORD,
  EDIT_ENDPOINT_DIALOG_ROLES,
  EDIT_ENDPOINT_DIALOG_ROLES_CHIP,
  EDIT_ENDPOINT_DIALOG_ROLES_ITEM,
  EDIT_ENDPOINT_DIALOG_SSO_SCOPE,
  EDIT_ENDPOINT_DIALOG_SUBMIT,
  EDIT_ENDPOINT_DIALOG_USERNAME,
  EDIT_ENDPOINT_DIALOG_USE_SSO,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { useServerRoles } from 'src/ui/containers/organization/hooks/serverRoles';
import { ConnectionStatus, clearConnection } from 'src/ui/features/connection';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useEditConnectionMutation,
  useListOrganizationConnectionsQuery,
  useSetOrganizationConnectionVisibilityMutation,
} from 'src/ui/graph/types';
import {
  useCurrentConnection,
  useProfile,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { editIndexSelector } from 'src/ui/selectors/connection';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(DialogType.EDIT_ENDPOINT);

export const EditEndpointDialog: React.VFC<{ isLaunchpad?: boolean }> = ({
  isLaunchpad,
}) => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const isOpen = useAppSelector(isOpenSelector);
  const editIndex = useAppSelector(editIndexSelector);

  const { data: profile } = useProfile();
  const { connection, isLoading: isConnectionLoading } = useCurrentConnection(
    editIndex.toString(),
    org
  );
  const invalidateConnection = useInvalidateConnection(
    org,
    connection?.id,
    connection?.index
  );

  const {
    data: connectionsData,
    isLoading: isConnectionsLoading,
  } = useListOrganizationConnectionsQuery(
    graphQLClient,
    { org_domain: org },
    { enabled: Boolean(org) }
  );

  const isLoading = isConnectionLoading || isConnectionsLoading;
  const didIsLoadingChange = useDidValueChange(isLoading);
  const didIsOpenChange = useDidValueChange(isOpen);

  const isCloudOrFree = Boolean(
    connection?.isStardogCloud || connection?.isStardogFree
  );
  const usesOrgSso = Boolean(connection?.usesOrgSso);

  const defaultRoles = React.useMemo(
    () =>
      (connectionsData?.listDefaultOrganizationConnectionsAccess || []).reduce<
        string[] | null
      >((acc, access) => {
        if (acc || !access?.connection || !connection) {
          return acc;
        }

        if (access.connection.id === connection.id) {
          return (
            access.stardog_roles?.filter((role): role is NonNullable<
              typeof role
            > => Boolean(role)) || []
          );
        }

        return acc;
      }, null),
    [connectionsData, connection]
  );

  const roleConnections = React.useMemo(
    () => (connection ? [connection as Connection] : []),
    [connection]
  );
  const rolesByConnectionId = useServerRoles(
    roleConnections,
    isOpen && isCloudOrFree
  );
  const rolesData = rolesByConnectionId[connection?.id || ''] || {};
  const roleItems = Array.from(rolesData.roles || []).map((role) => ({
    value: role,
    text: role,
  }));

  const {
    isLoading: isValidating,
    mutate: validate,
    reset: resetValidation,
  } = useVerifyConnection();
  const {
    isLoading: isSettingVisibility,
    mutate: setConnectionVisibility,
  } = useSetOrganizationConnectionVisibilityMutation(graphQLClient, {
    onMutate,
    onError,
  });
  const {
    isLoading: isEditingConnection,
    mutate: editConnection,
  } = useEditConnectionMutation(graphQLClient, {
    onMutate,
    onError,
  });
  const isEditing = isEditingConnection || isSettingVisibility;

  const [endpoint, setEndpoint] = React.useState('');
  const [internalEndpoint, setInternalEndpoint] = React.useState('');
  const [name, setName] = React.useState('');
  const [isDefault, setIsDefault] = React.useState(false);
  const [roles, setRoles] = React.useState<string[]>([]);
  const [useSSO, setUseSSO] = React.useState(false);
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [ssoScope, setSsoScope] = React.useState('');

  React.useEffect(() => {
    if (!(didIsLoadingChange || didIsOpenChange) || isLoading || !isOpen) {
      return;
    }

    if (connection) {
      setEndpoint(connection.endpoint);
      setInternalEndpoint(connection.internalEndpoint ?? '');
      setName(connection.name);
      setIsDefault(Boolean(defaultRoles));
      setRoles(defaultRoles || []);
      setUseSSO(connection.useSSO ?? false);
      setUsername(connection.useSSO ? '' : connection.username);
      setPassword('');
      setSsoScope(connection.ssoTokenExchangeScope ?? '');
    }
  }, [
    connection,
    defaultRoles,
    didIsLoadingChange,
    didIsOpenChange,
    isLoading,
    isOpen,
  ]);

  const handleClose = () => {
    dispatch(closedDialog());
    resetValidation();
    setEndpoint('');
    setInternalEndpoint('');
    setName('');
    setIsDefault(true);
    setRoles([]);
    setUseSSO(false);
    setUsername('');
    setPassword('');
    setSsoScope('');
  };

  const onSuccess = () => {
    invalidateConnection();
    dispatch(closedDialog());
    dispatch(clearConnection());
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    const trimmedUsername = username.trim();
    const trimmedScope = ssoScope.trim();

    if (
      !connection ||
      !trimmedName ||
      (trimmedUsername && !password) ||
      (!trimmedUsername && password) ||
      (usesOrgSso && !trimmedScope)
    ) {
      return;
    }

    // Test connection with new password, it is important that we remove the
    // existing token if there is one because the Stardog.js client will prefer
    // using that and we'll just get a 401 making the user super mad and throw
    // stuff. We don't want to be responsible for broken screens or coffee mugs.
    const newConnection = {
      endpoint: connection.endpoint,
      token: undefined,
      username: trimmedUsername,
      password,
      useSSO,
      name: trimmedName,
      status: ConnectionStatus.DISCONNECTED,
    };

    if (usesOrgSso) {
      // edit first then validate to retrieve the correct token
      editConnection(
        {
          input: {
            id: connection.id || '',
            org_domain: org,
            ssoTokenExchangeScope: trimmedScope,
          },
        },
        {
          onSuccess: () => {
            invalidateConnection();
            validate(
              { ...newConnection, id: connection.id, index: connection.index },
              { onError, onSuccess }
            );
          },
        }
      );
    } else if (org) {
      // validate first to retrieve updated token, then set visibility, then edit
      validate(newConnection, {
        onError,
        onSuccess: (validateData) => {
          setConnectionVisibility(
            {
              input: {
                connectionId: connection.id || '',
                org_domain: org,
                organization_is_enabled: isDefault,
                stardog_roles: roles,
              },
            },
            {
              onSuccess: (setConnectionData) => {
                if (
                  !setConnectionData.setOrganizationConnectionVisibility
                    ?.success
                ) {
                  onError(
                    new Error(
                      setConnectionData.setOrganizationConnectionVisibility
                        ?.error ||
                        // hard-coded to use same erro message as edit connection graphql response
                        'Failed to edit connection'
                    )
                  );
                  return;
                }
                editConnection(
                  {
                    input: {
                      id: connection.id || '',
                      name: trimmedName,
                      org_domain: org,
                      token: validateData.token,
                      username: trimmedUsername,
                    },
                  },
                  { onSuccess }
                );
              },
            }
          );
        },
      });
    } else {
      // validate first to retrieve updated token, then edit
      validate(newConnection, {
        onError,
        onSuccess: (validateData) => {
          editConnection(
            {
              input: {
                id: connection.id || '',
                internalEndpoint: internalEndpoint.trim() || undefined,
                name: trimmedName,
                org_domain: org,
                token: validateData.token,
                username: trimmedUsername,
              },
            },
            { onSuccess }
          );
        },
      });
    }
  };

  let submitTooltip = '';
  if (!name.trim()) {
    submitTooltip = copy.components.endpoints.inputs.name.validationRequired;
  } else if (usesOrgSso && !ssoScope.trim()) {
    submitTooltip =
      copy.components.endpoints.inputs.ssoScope.validationRequired;
  } else if (username && !password) {
    submitTooltip =
      copy.components.endpoints.inputs.password.validationRequiredWithUsername;
  } else if (!username && password) {
    submitTooltip =
      copy.components.endpoints.inputs.username.validationRequired;
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={EDIT_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.endpoints.editEndpointDialog.buttons.cancel}
          </Button>
          <Tooltip description={submitTooltip} disabled={!submitTooltip}>
            <Button
              disabled={isValidating || isEditing || Boolean(submitTooltip)}
              leftIcon={isValidating || isEditing ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={EDIT_ENDPOINT_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {copy.components.endpoints.editEndpointDialog.buttons.submit}
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.editEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            clearable={false}
            disabled={connection?.isStardogCloud}
            id="sd-connection-name"
            label={copy.components.endpoints.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            required={!connection?.isStardogCloud}
            size="md"
            testId={EDIT_ENDPOINT_DIALOG_NAME}
            value={name}
          />

          <Input
            clearable={false}
            disabled={connection?.isStardogCloud}
            id="sd-connection-endpoint"
            label={copy.components.endpoints.inputs.endpoint.label}
            onChange={(e) => setEndpoint(e.target.value)}
            required={!connection?.isStardogCloud}
            size="md"
            testId={EDIT_ENDPOINT_DIALOG_ENDPOINT}
            value={endpoint}
          />

          {usesOrgSso ? (
            <Input
              clearable={false}
              helpProps={{
                text: copy.components.endpoints.inputs.ssoScope.help,
              }}
              id="sd-connection-sso-scope"
              label={copy.components.endpoints.inputs.ssoScope.label}
              onChange={(e) => setSsoScope(e.target.value)}
              placeholder={
                copy.components.endpoints.inputs.ssoScope.placeholder
              }
              required
              size="md"
              testId={EDIT_ENDPOINT_DIALOG_SSO_SCOPE}
              value={ssoScope}
            />
          ) : null}

          {
            // default org roles toggle: shown only for non-org-SSO org connections
            org && !usesOrgSso ? (
              <Toggle
                align="right"
                checked={isDefault}
                className="endpoint-dialog-toggle"
                id="sd-connection-is-default"
                onChange={() => setIsDefault(!isDefault)}
                size="md"
                testId={EDIT_ENDPOINT_DIALOG_IS_DEFAULT}
              >
                {copy.components.endpoints.inputs.isDefault.label}
              </Toggle>
            ) : null
          }

          {
            // default org roles selector: cloud-hosted public org connections
            org && !usesOrgSso && isDefault && isCloudOrFree ? (
              <Selector.Multi<null, SelectItem, SelectItem[]>
                chipInputProps={{
                  chipTestId: EDIT_ENDPOINT_DIALOG_ROLES_CHIP,
                  className: 'endpoint-dialog-chip-selector',
                  clearable: false,
                  label: copy.components.endpoints.inputs.roles.defaultLabel,
                  size: 'md',
                  testId: EDIT_ENDPOINT_DIALOG_ROLES,
                  values: roles,
                }}
                creatable
                createText={copy.components.endpoints.inputs.roles.create}
                filterable
                items={roleItems}
                itemTestId={EDIT_ENDPOINT_DIALOG_ROLES_ITEM}
                loading={rolesData.isLoading}
                onSelect={(selected) =>
                  setRoles(selected.map((item) => item.value))
                }
                popoverProps={{
                  portalClassName: 'endpoint-dialog-menu-portal',
                }}
                selected={roles.map((role) => ({ value: role, text: role }))}
              />
            ) : null
          }

          {
            // cannot modify the connection type (SSO vs username/password) for
            // org connections (these are always SSO — cloud-hosted or org-SSO)
            // can only modify the SSO value for customers using the legacy IDP
            !org && profile?.profile?.is_legacy_customer_sso ? (
              <Toggle
                align="right"
                checked={useSSO}
                className="endpoint-dialog-toggle"
                id="sd-connection-use-sso"
                onChange={() => setUseSSO(!useSSO)}
                size="md"
                testId={EDIT_ENDPOINT_DIALOG_USE_SSO}
              >
                {copy.components.endpoints.inputs.useSSO.label}
              </Toggle>
            ) : null
          }

          {
            // only show username/password for non-sso connections
            useSSO ? null : (
              <>
                <Input
                  autoComplete="username"
                  clearable={false}
                  id="sd-connection-username"
                  label={copy.components.endpoints.inputs.username.label}
                  onChange={(e) => setUsername(e.target.value)}
                  size="md"
                  testId={EDIT_ENDPOINT_DIALOG_USERNAME}
                  value={username}
                />

                <Input
                  autoComplete="password"
                  clearable={false}
                  id="sd-connection-password"
                  label={copy.components.endpoints.inputs.password.label}
                  onChange={(e) => setPassword(e.target.value)}
                  size="md"
                  testId={EDIT_ENDPOINT_DIALOG_PASSWORD}
                  type="password"
                  value={password}
                />
              </>
            )
          }

          {isLaunchpad ? (
            <>
              <div className="endpoint-dialog-divider" />

              <div className="endpoint-dialog-container">
                <Input
                  clearable={false}
                  id="sd-connection-internal-endpoint"
                  label={
                    copy.components.endpoints.inputs.internalEndpoint.label
                  }
                  onChange={(e) => setInternalEndpoint(e.target.value)}
                  placeholder={
                    copy.components.endpoints.inputs.internalEndpoint
                      .placeholder
                  }
                  size="md"
                  testId={EDIT_ENDPOINT_DIALOG_INTERNAL_ENDPOINT}
                  value={internalEndpoint}
                />
                <Body className="endpoint-dialog-input-footer" size="sm">
                  {copy.components.endpoints.inputs.internalEndpoint.help}
                </Body>
              </div>
            </>
          ) : null}

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
